/**
 * Inventory Management API Module
 * Handles Farm, Strain, Location, Container (Hemp Record), PreBatch (Sock),
 * Batch, BatchProduct (Storage), StorageVessel, ProductionRun, and Reconciliation
 */

import { type RequestFunction } from './types'

// ============================================================================
// TYPES
// ============================================================================

export interface Farm {
  id: number
  name: string
  uuid: string
  business?: number | null
  total_container_weight: number
  available_container_weight: number
}

export interface Strain {
  id: number
  name: string
  terpene: 0 | 1 | 2 // 0=Indica, 1=Sativa, 2=Hybrid
  terpene_display: string
  uuid: string
  created_by?: number
}

export interface Location {
  id: number
  name: string
  uuid: string
  barcode: string
  type?: 'harvest' | 'frozen-storage' | 'manufacturing' | 'inventory' | null
  related_counts: {
    containers: number
    prebatches: number
    batches: number
    batch_products: number
  }
}

export interface PreBatchCategory {
  slug: string
  name: string
  uuid: string
  weight: number
}

export interface StorageVessel {
  id: number
  name: string
  uuid: string
  tare_weight: number
  fluid_ounces?: number | null
}

// Barcode Handler
export interface BarcodeAction {
  url: string
  text: string
}

export interface BarcodeHandlerResponse {
  actions: BarcodeAction[]
  data: {
    strain: string | null
    farm: string | null
    location: string | null
    weight: string | null
    status: string | null
    barcode: string
    label: string | null
  }
}

export interface UpdateLocationResponse {
  success: boolean
  object_type: string
  object_id: number
  new_location: { id: number; name: string }
}

export interface BatchSampleResponse {
  id: number
  batch_id: number
  weight: number
  notes: string | null
  timestamp: string
}

export interface BatchSelectJarResponse {
  id: number
  batch_product_id: number
  weight: number
  vessel: { id: number; name: string }
}

export interface Container {
  id: number
  barcode: string
  strain: number
  strain_name: string
  strain_terpene: string
  weight: number
  farm: number
  farm_name: string
  harvest_plan?: number | null
  tolling_contract?: number | null
  metadata: Record<string, unknown>
  operational_context: 'harvest' | 'tolling' | 'rnd' | 'legacy'
  total_pallet_weight: number
  effective_weight: number
  qr_code: string
}

export interface ContainerCreate {
  strain_name: string
  weight?: number
  harvest_plan?: number
  farm?: number
  location?: number
  heavy_metals?: 0 | 1 | 2
  pesticides?: 0 | 1 | 2
  cbd?: number
  thc?: number
  container_type?: string
  other_issues?: string
  notes?: string
  unit_price?: number
  metadata?: Record<string, unknown>
}

export interface PreBatch {
  id: number
  barcode: string
  container: number
  container_barcode: string
  container_farm: string
  container_strain: string
  batch?: number | null
  batch_barcode?: string | null
  weight: number
  location?: number | null
  location_name?: string | null
  category?: string | null
  category_name?: string | null
  ts: string
  updated_ts: string
  uuid: string
}

export interface PreBatchCreate {
  container: number
  weight: number
  location?: number
  category?: string
}

export interface Batch {
  id: number
  barcode: string
  status: string
  input_weight: number
  output_weight: number
  tare_weight: number
  net_output_weight: number
  available_weight: number
  crude_yield_percent: number
  farm?: number | null
  farm_name?: string | null
  strain?: number | null
  strain_name?: string | null
  location?: number | null
  location_name?: string | null
  quarantine: boolean
  verified: boolean
  archive: boolean
  ts: string
  uuid: string
  prebatch_count: number
}

export interface BatchDetail extends Batch {
  solvent?: number | null
  purges: Array<{ timestamp: string; value: number }>
  sample_weight: number
  net_purged_weight: number
  consumed_weight: number
  purged_weight: number
  purged_yield_percent: number
  container?: number | null
  parent?: number | null
  certificate?: string | null
  notes: Record<string, unknown>
  verification_status_display: string
  prebatches: PreBatch[]
  batch_product_items: BatchProductItem[]
  farm_detail?: Farm | null
  strain_detail?: Strain | null
  location_detail?: Location | null
}

export interface BatchCreate {
  container?: number
  farm?: number
  strain?: number
  location?: number
  parent?: number
  input_weight?: number
  output_weight?: number
  tare_weight?: number
  solvent?: number
  notes?: Record<string, unknown>
}

export interface BatchUpdate {
  status?: string
  output_weight?: number
  tare_weight?: number
  sample_weight?: number
  location?: number
  quarantine?: boolean
  verified?: boolean
  archive?: boolean
  certificate?: string
  notes?: Record<string, unknown>
}

export interface BatchProductItem {
  id: number
  batch: number
  batch_barcode: string
  batch_product: number
  weight: number
  ts: string
  uuid: string
}

// Jar/Storage status types for inventory management
export type JarStatus = 'sellable' | 'quarantine' | 'rnd'

export interface StatusHistoryEntry {
  status: JarStatus
  changed_to: JarStatus
  by: string
  at: string
  notes?: string
}

export interface ActivityLogEntry {
  timestamp: string
  user: string
  action: 'created' | 'weight_adjusted' | 'status_changed' | 'transferred' | 'added_to_processing' | 'batch_added' | 'location_updated'
  details: string
  metadata?: Record<string, unknown>
}

export interface WeightAdjustment {
  id: number
  adjustment_session_id: number
  adjustment_session_uuid: string
  adjustment_item_id: number
  original_gross_weight: number
  gross_weight: number
  old_weight: number
  new_weight: number
  adjustment_amount: number
  reason: 'verification' | 'correction' | 'reconciliation' | 'other'
  notes?: string
  adjusted_by: number | null
  adjusted_by_username: string | null
  adjusted_at: string
}

// Jar-to-Jar Transfer types
export interface JarTransfer {
  id: number
  source_jar: number
  source_jar_barcode: string
  destination_jar: number
  destination_jar_barcode: string
  weight: number
  notes?: string
  transferred_by: string
  transferred_at: string
}

export interface JarTransferHistory {
  transfers_out: JarTransfer[]
  transfers_in: JarTransfer[]
  total_out: number
  total_in: number
}

export interface BatchProduct {
  id: number
  barcode: string
  weight: number
  tare_weight: number
  net_weight: number
  available_weight: number
  oil_type: string
  status: JarStatus // NEW: status field
  farm?: number | null
  farm_name?: string | null
  strain?: number | null
  strain_name?: string | null
  location?: number | null
  location_name?: string | null
  vessel?: number | null
  vessel_name?: string | null
  gross_weight?: number | null
  parent_batch_barcode?: string | null
  parent_batch_id?: number | null
  archive: boolean
  ts: string
  uuid: string
}

export interface BatchProductDetail extends BatchProduct {
  gross_weight: number
  consumed_weight_value: number
  sum_input_weight: number
  container?: number | null
  processing?: number | null
  source?: number | null
  notes: Record<string, unknown> & {
    status_history?: StatusHistoryEntry[]
    activity?: ActivityLogEntry[]
    adjustments?: WeightAdjustment[]
  }
  batch_items: BatchProductItem[]
  farm_detail?: Farm | null
  strain_detail?: Strain | null
  location_detail?: Location | null
  vessel_detail?: StorageVessel | null
  // Base64 encoded PNG barcode image from backend (barcode_img_v2)
  barcode_image_base64?: string | null
}

export interface BatchProductCreate {
  weight?: number
  tare_weight?: number
  oil_type?: string
  farm?: number
  strain?: number
  container?: number
  location?: number
  vessel?: number
  notes?: Record<string, unknown>
}

export interface ProductionRunItem {
  id: number
  production_run: number
  batch_product: number
  batch_product_barcode: string
  batch_product_oil_type: string
  weight: number
  uuid: string
  ts: string
  created_by?: number
}

export interface ProductionRun {
  id: number
  barcode: string
  product: number
  product_name: string
  quantity: number
  final_quantity?: number | null
  status: string
  completed: boolean
  completed_ts?: string | null
  template: boolean
  priority: number
  target_date?: string | null
  assignee?: number | null
  assignee_name?: string | null
  total_oil_weight: number
  available_quantity: number
  ts: string
  uuid: string
}

export interface ProductionRunDetail extends ProductionRun {
  product_detail?: {
    id: number
    name: string
    barcode: string
    price: number
  } | null
  assignee_detail?: {
    id: number
    username: string
    full_name: string
  } | null
  order_item?: number | null
  notes: Record<string, unknown>
  items: ProductionRunItem[]
}

export interface ProductionRunCreate {
  product: number
  quantity: number
  template?: boolean
  priority?: number
  target_date?: string
  assignee?: number
  order_item?: number
}

export interface ProductionRunUpdate {
  quantity?: number
  final_quantity?: number
  status?: string
  completed?: boolean
  completed_ts?: string
  priority?: number
  target_date?: string
  assignee?: number
  notes?: Record<string, unknown>
}

export interface ReconciliationUploadResult {
  message: string
  entity_type: string
  items: Array<Record<string, unknown>>
  columns: string[]
}

export interface ReconciliationCompareResult {
  entity_type: string
  items_to_add: Array<Record<string, unknown>>
  items_to_archive: Array<{ id: number; barcode: string; name?: string }>
  items_to_update: Array<{
    id: number
    barcode: string
    name?: string
    differences: Array<{ field: string; file_value: unknown; db_value: unknown }>
  }>
  items_unchanged: Array<{ id: number; barcode: string; name?: string }>
  summary: {
    total_in_file: number
    total_in_database: number
    to_add: number
    to_archive: number
    to_update: number
    unchanged: number
  }
}

export interface ReconciliationApplyResult {
  message: string
  results: {
    added: number
    archived: number
    updated: number
    errors: Array<{ action: unknown; error: string }>
  }
}

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface InventoryDashboardStats {
  hemp_records: number
  socks: number
  batches: {
    total: number
    status_breakdown: Record<string, number>
  }
  storage: number
  vessels: {
    total: number
  }
  products: number
  production_runs: {
    total: number
    status_breakdown: Record<string, number>
  }
}

export interface FilterParams {
  page?: number
  page_size?: number
  search?: string
  ordering?: string
  [key: string]: unknown
}

// ============================================================================
// API CLASS
// ============================================================================

export class InventoryManagementApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // FARMS
  // ============================================================================

  async getFarms(params?: FilterParams): Promise<PaginatedResponse<Farm>> {
    return this.request('/api/inventory/farms/', { params })
  }

  async getFarm(id: number): Promise<Farm> {
    return this.request(`/api/inventory/farms/${id}/`)
  }

  async createFarm(data: { name: string; business?: number }): Promise<Farm> {
    return this.request('/api/inventory/farms/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateFarm(id: number, data: Partial<{ name: string; business?: number }>): Promise<Farm> {
    return this.request(`/api/inventory/farms/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteFarm(id: number): Promise<void> {
    return this.request(`/api/inventory/farms/${id}/`, { method: 'DELETE' })
  }

  // ============================================================================
  // STRAINS
  // ============================================================================

  async getStrains(params?: FilterParams): Promise<PaginatedResponse<Strain>> {
    return this.request('/api/inventory/strains/', { params })
  }

  async getStrain(id: number): Promise<Strain> {
    return this.request(`/api/inventory/strains/${id}/`)
  }

  async createStrain(data: { name: string; terpene: 0 | 1 | 2 }): Promise<Strain> {
    return this.request('/api/inventory/strains/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateStrain(id: number, data: Partial<{ name: string; terpene: 0 | 1 | 2 }>): Promise<Strain> {
    return this.request(`/api/inventory/strains/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteStrain(id: number): Promise<void> {
    return this.request(`/api/inventory/strains/${id}/`, { method: 'DELETE' })
  }

  // ============================================================================
  // LOCATIONS
  // ============================================================================

  async getLocations(params?: FilterParams): Promise<PaginatedResponse<Location>> {
    return this.request('/api/inventory/locations/', { params })
  }

  async getLocation(id: number): Promise<Location> {
    return this.request(`/api/inventory/locations/${id}/`)
  }

  async getLocationItems(id: number): Promise<{
    containers: Array<{ id: number; barcode: string }>
    prebatches: Array<{ id: number; barcode: string }>
    batches: Array<{ id: number; barcode: string }>
    batch_products: Array<{ id: number; barcode: string }>
  }> {
    return this.request(`/api/inventory/locations/${id}/items/`)
  }

  async createLocation(data: { name: string; type?: string }): Promise<Location> {
    return this.request('/api/inventory/locations/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateLocation(id: number, data: Partial<{ name: string; type?: string }>): Promise<Location> {
    return this.request(`/api/inventory/locations/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteLocation(id: number): Promise<void> {
    return this.request(`/api/inventory/locations/${id}/`, { method: 'DELETE' })
  }

  // ============================================================================
  // STORAGE VESSELS
  // ============================================================================

  async getVessels(params?: FilterParams): Promise<PaginatedResponse<StorageVessel>> {
    return this.request('/api/inventory/vessels/', { params })
  }

  async getVessel(id: number): Promise<StorageVessel> {
    return this.request(`/api/inventory/vessels/${id}/`)
  }

  async createVessel(data: { name: string; tare_weight?: number; fluid_ounces?: number }): Promise<StorageVessel> {
    return this.request('/api/inventory/vessels/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateVessel(id: number, data: Partial<{ name: string; tare_weight?: number; fluid_ounces?: number }>): Promise<StorageVessel> {
    return this.request(`/api/inventory/vessels/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteVessel(id: number): Promise<void> {
    return this.request(`/api/inventory/vessels/${id}/`, { method: 'DELETE' })
  }

  // ============================================================================
  // PREBATCH CATEGORIES
  // ============================================================================

  async getPreBatchCategories(params?: FilterParams): Promise<PaginatedResponse<PreBatchCategory>> {
    return this.request('/api/inventory/prebatch-categories/', { params })
  }

  async getPreBatchCategory(slug: string): Promise<PreBatchCategory> {
    return this.request(`/api/inventory/prebatch-categories/${slug}/`)
  }

  async createPreBatchCategory(data: { slug: string; name: string; weight?: number }): Promise<PreBatchCategory> {
    return this.request('/api/inventory/prebatch-categories/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updatePreBatchCategory(slug: string, data: Partial<{ name: string; weight?: number }>): Promise<PreBatchCategory> {
    return this.request(`/api/inventory/prebatch-categories/${slug}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deletePreBatchCategory(slug: string): Promise<void> {
    return this.request(`/api/inventory/prebatch-categories/${slug}/`, { method: 'DELETE' })
  }

  // ============================================================================
  // PREBATCHES (SOCKS)
  // ============================================================================

  async getSocks(params?: FilterParams): Promise<PaginatedResponse<PreBatch>> {
    return this.request('/api/inventory/socks/', { params })
  }

  async getSock(id: number): Promise<PreBatch> {
    return this.request(`/api/inventory/socks/${id}/`)
  }

  async createSock(data: PreBatchCreate): Promise<PreBatch> {
    return this.request('/api/inventory/socks/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateSock(id: number, data: Partial<PreBatchCreate>): Promise<PreBatch> {
    return this.request(`/api/inventory/socks/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteSock(id: number): Promise<void> {
    return this.request(`/api/inventory/socks/${id}/`, { method: 'DELETE' })
  }

  async batchAssignSocks(sockIds: number[], batchId: number): Promise<{ message: string; batch_barcode: string; sock_count: number }> {
    return this.request('/api/inventory/socks/batch_assign/', {
      method: 'POST',
      body: JSON.stringify({ sock_ids: sockIds, batch_id: batchId })
    })
  }

  async bulkCreateSocks(data: {
    container_id: number
    count?: number
    weight?: number
    location_id?: number
    category?: string
  }): Promise<{ message: string; socks: PreBatch[] }> {
    return this.request('/api/inventory/socks/bulk_create/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async bulkArchiveSocks(sockIds: number[]): Promise<{ message: string }> {
    return this.request('/api/inventory/socks/bulk_archive/', {
      method: 'POST',
      body: JSON.stringify({ sock_ids: sockIds })
    })
  }

  // ============================================================================
  // BATCHES
  // ============================================================================

  async getBatches(params?: FilterParams): Promise<PaginatedResponse<Batch>> {
    return this.request('/api/inventory/batches/', { params })
  }

  async getBatch(id: number): Promise<BatchDetail> {
    return this.request(`/api/inventory/batches/${id}/`)
  }

  async createBatch(data: BatchCreate): Promise<BatchDetail> {
    return this.request('/api/inventory/batches/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateBatch(id: number, data: BatchUpdate): Promise<BatchDetail> {
    return this.request(`/api/inventory/batches/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteBatch(id: number): Promise<void> {
    return this.request(`/api/inventory/batches/${id}/`, { method: 'DELETE' })
  }

  async addBatchPurge(id: number, value: number): Promise<{ message: string; purge_count: number; purged_weight: number }> {
    return this.request(`/api/inventory/batches/${id}/add_purge/`, {
      method: 'POST',
      body: JSON.stringify({ value })
    })
  }

  async verifyBatch(id: number): Promise<{ message: string; verified: boolean }> {
    return this.request(`/api/inventory/batches/${id}/verify/`, { method: 'POST' })
  }

  async toggleBatchQuarantine(id: number): Promise<{ message: string; quarantine: boolean }> {
    return this.request(`/api/inventory/batches/${id}/quarantine/`, { method: 'POST' })
  }

  async transitionBatchStatus(id: number, status: string): Promise<{ message: string; old_status: string; new_status: string }> {
    return this.request(`/api/inventory/batches/${id}/status_transition/`, {
      method: 'POST',
      body: JSON.stringify({ status })
    })
  }

  async splitBatch(id: number, outputWeight?: number, tareWeight?: number): Promise<{ message: string; batch: BatchDetail }> {
    return this.request(`/api/inventory/batches/${id}/split/`, {
      method: 'POST',
      body: JSON.stringify({ output_weight: outputWeight, tare_weight: tareWeight })
    })
  }

  async bulkUpdateBatchStatus(batchIds: number[], status: string): Promise<{ message: string }> {
    return this.request('/api/inventory/batches/bulk_status/', {
      method: 'POST',
      body: JSON.stringify({ batch_ids: batchIds, status })
    })
  }

  async handleBarcode(barcode: string): Promise<BarcodeHandlerResponse> {
    return this.request('/api/barcode-handler/', {
      method: 'POST',
      body: JSON.stringify({ barcode })
    })
  }

  async updateItemLocation(barcode: string, location_id: number): Promise<UpdateLocationResponse> {
    return this.request('/api/inventory/update-location/', {
      method: 'POST',
      body: JSON.stringify({ barcode, location_id })
    })
  }

  async sampleBatch(id: number, data: { weight: number; notes?: string }): Promise<BatchSampleResponse> {
    return this.request(`/api/inventory/batches/${id}/sample/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async selectJar(id: number, data: { vessel_id: number; weight: number }): Promise<BatchSelectJarResponse> {
    return this.request(`/api/inventory/batches/${id}/select-jar/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // ============================================================================
  // BATCH PRODUCTS (STORAGE/OIL)
  // ============================================================================

  async getStorage(params?: FilterParams): Promise<PaginatedResponse<BatchProduct>> {
    return this.request('/api/inventory/storage/', { params })
  }

  async getStorageItem(id: number): Promise<BatchProductDetail> {
    return this.request(`/api/inventory/storage/${id}/`)
  }

  async createStorageItem(data: BatchProductCreate): Promise<BatchProductDetail> {
    return this.request('/api/inventory/storage/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateStorageItem(id: number, data: Partial<BatchProductCreate>): Promise<BatchProductDetail> {
    return this.request(`/api/inventory/storage/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteStorageItem(id: number): Promise<void> {
    return this.request(`/api/inventory/storage/${id}/`, { method: 'DELETE' })
  }

  async transferStorageItem(id: number, locationId: number): Promise<{ message: string; new_location: { id: number; name: string } }> {
    return this.request(`/api/inventory/storage/${id}/transfer/`, {
      method: 'POST',
      body: JSON.stringify({ location_id: locationId })
    })
  }

  async archiveStorageItem(id: number): Promise<{ message: string }> {
    return this.request(`/api/inventory/storage/${id}/archive/`, { method: 'POST' })
  }

  async unarchiveStorageItem(id: number): Promise<{ message: string }> {
    return this.request(`/api/inventory/storage/${id}/unarchive/`, { method: 'POST' })
  }

  async bulkTransferStorage(itemIds: number[], locationId: number): Promise<{ message: string }> {
    return this.request('/api/inventory/storage/bulk_transfer/', {
      method: 'POST',
      body: JSON.stringify({ item_ids: itemIds, location_id: locationId })
    })
  }

  // ============================================================================
  // STORAGE STATUS MANAGEMENT (Story 1.1)
  // ============================================================================

  /**
   * Update the status of a storage item (jar)
   * Status options: 'sellable', 'quarantine', 'rnd'
   */
  async updateStorageStatus(
    id: number,
    status: JarStatus,
    notes?: string
  ): Promise<{ message: string; status: JarStatus }> {
    return this.request(`/api/inventory/storage/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ status, status_notes: notes })
    })
  }

  /**
   * Get status change history for a storage item
   */
  async getStorageStatusHistory(id: number): Promise<StatusHistoryEntry[]> {
    return this.request(`/api/inventory/storage/${id}/status-history/`)
  }

  // ============================================================================
  // STORAGE WEIGHT ADJUSTMENTS (Story 1.3)
  // ============================================================================

  /**
   * Adjust the weight of a storage item with reason tracking.
   *
   * This mirrors the legacy Django admin BatchProductAdjustment workflow,
   * creating proper database records for audit trail and compatibility.
   *
   * Supports three input modes (provide exactly one):
   * - gross_weight: New measured gross weight including jar tare (legacy mode)
   * - adjustment_amount: Delta to apply to net weight (e.g., +5 or -10)
   * - new_weight: Absolute target net weight (convenience mode)
   */
  async adjustStorageWeight(
    id: number,
    data: {
      gross_weight?: number
      adjustment_amount?: number
      new_weight?: number
      reason: 'verification' | 'correction' | 'reconciliation' | 'other'
      notes?: string
    }
  ): Promise<{
    message: string
    adjustment: WeightAdjustment
    adjustment_session_id: number
    adjustment_session_uuid: string
    barcode: string
    original_gross_weight: number
    gross_weight: number
    old_weight: number
    new_weight: number
    adjustment_amount: number
    reason: string
    current_weight: number
  }> {
    return this.request(`/api/inventory/storage/${id}/adjust/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Get weight adjustment history for a storage item
   */
  async getStorageAdjustments(id: number): Promise<{
    barcode: string
    current_weight: number
    adjustments: WeightAdjustment[]
    total_adjustments: number
  }> {
    return this.request(`/api/inventory/storage/${id}/adjustments/`)
  }

  // ============================================================================
  // STORAGE ACTIVITY LOG (Story 1.2)
  // ============================================================================

  /**
   * Get activity log for a storage item
   */
  async getStorageActivity(id: number): Promise<ActivityLogEntry[]> {
    return this.request(`/api/inventory/storage/${id}/activity/`)
  }

  /**
   * Add a batch to a storage item
   */
  async addBatchToStorage(
    id: number,
    data: { batch_id: number; weight: number }
  ): Promise<{ message: string; item: BatchProductItem }> {
    return this.request(`/api/inventory/storage/${id}/add-batch/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Add storage item to processing
   */
  async addToProcessing(
    id: number,
    processingId: number
  ): Promise<{ message: string }> {
    return this.request(`/api/inventory/storage/${id}/add-to-processing/`, {
      method: 'POST',
      body: JSON.stringify({ processing_id: processingId })
    })
  }

  /**
   * Update storage item location
   */
  async updateStorageLocation(
    id: number,
    locationId: number
  ): Promise<{ message: string; location: { id: number; name: string } }> {
    return this.request(`/api/inventory/storage/${id}/location/`, {
      method: 'PATCH',
      body: JSON.stringify({ location_id: locationId })
    })
  }

  // ============================================================================
  // JAR-TO-JAR TRANSFERS (Story 1.4)
  // ============================================================================

  /**
   * Transfer oil FROM this jar TO another jar
   * Reduces weight in source jar, increases in destination jar
   */
  async transferFromJar(
    id: number,
    data: {
      destination_jar_id: number
      transfer_weight: number
      notes?: string
    }
  ): Promise<{ message: string; transfer: JarTransfer }> {
    return this.request(`/api/inventory/storage/${id}/transfer_from/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Transfer oil TO this jar FROM another jar
   * Reduces weight in source jar, increases in destination jar
   */
  async transferToJar(
    id: number,
    data: {
      source_jar_id: number
      transfer_weight: number
      notes?: string
    }
  ): Promise<{ message: string; transfer: JarTransfer }> {
    return this.request(`/api/inventory/storage/${id}/transfer_to/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Get transfer history for a storage item
   * Returns both transfers in and transfers out
   */
  async getJarTransferHistory(id: number): Promise<JarTransferHistory> {
    return this.request(`/api/inventory/storage/${id}/transfers/`)
  }

  /**
   * Search for jars by barcode (for transfer destination/source selection)
   */
  async searchJars(query: string, excludeId?: number): Promise<BatchProduct[]> {
    const params: Record<string, string | number> = { search: query, page_size: 10 }
    if (excludeId) {
      params.exclude = excludeId
    }
    const response = await this.request<PaginatedResponse<BatchProduct> | BatchProduct[]>(
      '/api/inventory/storage/',
      { params }
    )
    // Handle both array and paginated response
    return Array.isArray(response) ? response : (response.results || [])
  }

  /**
   * Bulk fetch storage items by barcodes for Excel reconciliation.
   * This is more efficient than fetching all storage and filtering client-side.
   */
  async bulkFetchStorage(barcodes: string[]): Promise<{
    items: BatchProduct[]
    count: number
    not_found: string[]
  }> {
    return this.request('/api/inventory/storage/bulk_fetch/', {
      method: 'POST',
      body: JSON.stringify({ barcodes })
    })
  }

  // ============================================================================
  // PRODUCTION RUNS
  // ============================================================================

  async getProductionRuns(params?: FilterParams): Promise<PaginatedResponse<ProductionRun>> {
    return this.request('/api/inventory/production-runs/', { params })
  }

  async getProductionRun(id: number): Promise<ProductionRunDetail> {
    return this.request(`/api/inventory/production-runs/${id}/`)
  }

  async createProductionRun(data: ProductionRunCreate): Promise<ProductionRunDetail> {
    return this.request('/api/inventory/production-runs/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateProductionRun(id: number, data: ProductionRunUpdate): Promise<ProductionRunDetail> {
    return this.request(`/api/inventory/production-runs/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteProductionRun(id: number): Promise<void> {
    return this.request(`/api/inventory/production-runs/${id}/`, { method: 'DELETE' })
  }

  async startProductionRun(id: number): Promise<{ message: string; status: string }> {
    return this.request(`/api/inventory/production-runs/${id}/start/`, { method: 'POST' })
  }

  async completeProductionRun(id: number, finalQuantity?: number): Promise<{ message: string; final_quantity: number; status: string }> {
    return this.request(`/api/inventory/production-runs/${id}/complete/`, {
      method: 'POST',
      body: JSON.stringify({ final_quantity: finalQuantity })
    })
  }

  async addProductionRunInput(id: number, batchProductId: number, weight: number): Promise<{ message: string; item: ProductionRunItem }> {
    return this.request(`/api/inventory/production-runs/${id}/add_input/`, {
      method: 'POST',
      body: JSON.stringify({ batch_product_id: batchProductId, weight })
    })
  }

  async removeProductionRunInput(id: number, itemId: number): Promise<{ message: string }> {
    return this.request(`/api/inventory/production-runs/${id}/remove_input/`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId })
    })
  }

  // ============================================================================
  // RECONCILIATION
  // ============================================================================

  async uploadReconciliationFile(file: File, entityType: string): Promise<ReconciliationUploadResult> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('entity_type', entityType)

    return this.request('/api/inventory/reconciliation/upload/', {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for multipart
    })
  }

  async compareReconciliation(items: Array<Record<string, unknown>>, entityType: string): Promise<ReconciliationCompareResult> {
    return this.request('/api/inventory/reconciliation/compare/', {
      method: 'POST',
      body: JSON.stringify({ items, entity_type: entityType })
    })
  }

  async applyReconciliation(
    entityType: string,
    actions: Array<{ action: 'add' | 'archive' | 'update'; data: Record<string, unknown> }>
  ): Promise<ReconciliationApplyResult> {
    return this.request('/api/inventory/reconciliation/apply/', {
      method: 'POST',
      body: JSON.stringify({ entity_type: entityType, actions })
    })
  }

  getReconciliationTemplateUrl(entityType: string): string {
    return `/api/inventory/reconciliation/template/?entity_type=${entityType}`
  }

  // ============================================================================
  // DASHBOARD STATS
  // ============================================================================

  async getDashboardStats(): Promise<InventoryDashboardStats> {
    return this.request('/api/inventory/dashboard/')
  }
}
